/**
 * Intake Flow Templates
 *
 * System templates for loan intake wizards.
 * Each template defines a multi-step intake flow for a specific loan type.
 *
 * @see Issue #432 - Templated Kiavi-Style Loan Intake Wizard
 */

import {
  type IntakeComplianceQuestion,
  IntakeDisplayMode,
  type IntakeFieldTemplate,
  type IntakeFlowTemplate,
  type IntakeFlowType,
  type IntakeStepTemplate,
} from './types';

// =============================================================================
// TEMPLATE IDS
// =============================================================================

export const INTAKE_FLOW_TEMPLATE_IDS = {
  BRIDGE_FIX_FLIP: 'tpl-intake-bridge-fix-flip',
  CONSTRUCTION: 'tpl-intake-construction',
  RENTAL_DSCR: 'tpl-intake-rental-dscr',
  COMMERCIAL: 'tpl-intake-commercial',
  GENERAL: 'tpl-intake-general',
} as const;

// =============================================================================
// SHARED STEPS (Reused across templates)
// =============================================================================

const createInvestmentTypeStep = (): IntakeStepTemplate => ({
  id: 'step-investment-type',
  order: 0,
  name: 'Investment Type',
  description: 'Select your investment strategy',
  icon: 'AccountBalance',
  requiresAuth: false,
  fields: [
    {
      id: 'field-investment-type',
      name: 'investmentType',
      label: 'What type of investment is this?',
      type: 'radio',
      required: true,
      options: [
        { value: 'bridge', label: 'Bridge / Fix & Flip' },
        { value: 'construction', label: 'Ground-Up Construction' },
        { value: 'rental', label: 'DSCR Rental' },
        { value: 'commercial', label: 'Commercial' },
        { value: 'general', label: "Not Sure - I'd like to speak with an advisor" },
      ],
      width: 12,
    },
  ],
});

const createContactInfoStep = (): IntakeStepTemplate => ({
  id: 'step-contact-info',
  order: 1,
  name: 'Contact Information',
  description: "Tell us how to reach you and we'll have an advisor contact you",
  icon: 'ContactPhone',
  requiresAuth: false,
  fields: [
    {
      id: 'field-first-name',
      name: 'firstName',
      label: 'First Name',
      type: 'text',
      required: true,
      width: 6,
    },
    {
      id: 'field-last-name',
      name: 'lastName',
      label: 'Last Name',
      type: 'text',
      required: true,
      width: 6,
    },
    {
      id: 'field-email',
      name: 'email',
      label: 'Email Address',
      type: 'email',
      required: true,
      width: 6,
    },
    {
      id: 'field-phone',
      name: 'phone',
      label: 'Phone Number',
      type: 'phone',
      required: true,
      width: 6,
    },
    {
      id: 'field-preferred-contact',
      name: 'preferredContactMethod',
      label: 'Preferred Contact Method',
      type: 'radio',
      required: false,
      options: [
        { value: 'email', label: 'Email' },
        { value: 'phone', label: 'Phone' },
        { value: 'text', label: 'Text Message' },
      ],
      width: 12,
    },
  ],
});

const createEntityExperienceStep = (): IntakeStepTemplate => ({
  id: 'step-entity-experience',
  order: 2,
  name: 'Entity & Experience',
  description: 'Tell us about your entity and experience',
  icon: 'Business',
  requiresAuth: false,
  fields: [
    {
      id: 'field-entity-type',
      name: 'entityType',
      label: 'Entity Type',
      type: 'select',
      required: true,
      options: [
        { value: 'individual', label: 'Individual' },
        { value: 'llc', label: 'LLC' },
        { value: 'corporation', label: 'Corporation' },
        { value: 'trust', label: 'Trust' },
        { value: 'partnership', label: 'Partnership' },
      ],
      helpText: 'How will the property be held?',
      width: 6,
    },
    {
      id: 'field-entity-name',
      name: 'entityName',
      label: 'Entity Name',
      type: 'text',
      required: false,
      placeholder: 'Your LLC or Corporation name',
      conditionalDisplay: {
        field: 'entityType',
        operator: 'not_equals',
        value: 'individual',
      },
      width: 6,
    },
    {
      id: 'field-number-of-owners',
      name: 'numberOfOwners',
      label: 'Number of Entity Owners',
      type: 'select',
      required: false,
      options: [
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
      ],
      conditionalDisplay: {
        field: 'entityType',
        operator: 'not_equals',
        value: 'individual',
      },
      width: 6,
    },
    // Owner 1 fields (shown when numberOfOwners >= 1 AND entityType != individual)
    {
      id: 'field-owner1-name',
      name: 'owner1Name',
      label: 'Owner 1 Name',
      type: 'text',
      required: false,
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '1' },
        ],
      },
      width: 4,
    },
    {
      id: 'field-owner1-percentage',
      name: 'owner1Percentage',
      label: 'Ownership %',
      type: 'number',
      required: false,
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '1' },
        ],
      },
      width: 4,
    },
    {
      id: 'field-owner1-role',
      name: 'owner1Role',
      label: 'Role',
      type: 'select',
      required: false,
      options: [
        { value: 'managing_member', label: 'Managing Member' },
        { value: 'member', label: 'Member' },
        { value: 'general_partner', label: 'General Partner' },
        { value: 'limited_partner', label: 'Limited Partner' },
        { value: 'trustee', label: 'Trustee' },
        { value: 'officer', label: 'Officer' },
        { value: 'shareholder', label: 'Shareholder' },
      ],
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '1' },
        ],
      },
      width: 4,
    },
    // Owner 2 fields
    {
      id: 'field-owner2-name',
      name: 'owner2Name',
      label: 'Owner 2 Name',
      type: 'text',
      required: false,
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '2' },
        ],
      },
      width: 4,
    },
    {
      id: 'field-owner2-percentage',
      name: 'owner2Percentage',
      label: 'Ownership %',
      type: 'number',
      required: false,
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '2' },
        ],
      },
      width: 4,
    },
    {
      id: 'field-owner2-role',
      name: 'owner2Role',
      label: 'Role',
      type: 'select',
      required: false,
      options: [
        { value: 'managing_member', label: 'Managing Member' },
        { value: 'member', label: 'Member' },
        { value: 'general_partner', label: 'General Partner' },
        { value: 'limited_partner', label: 'Limited Partner' },
        { value: 'trustee', label: 'Trustee' },
        { value: 'officer', label: 'Officer' },
        { value: 'shareholder', label: 'Shareholder' },
      ],
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '2' },
        ],
      },
      width: 4,
    },
    // Owner 3 fields
    {
      id: 'field-owner3-name',
      name: 'owner3Name',
      label: 'Owner 3 Name',
      type: 'text',
      required: false,
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '3' },
        ],
      },
      width: 4,
    },
    {
      id: 'field-owner3-percentage',
      name: 'owner3Percentage',
      label: 'Ownership %',
      type: 'number',
      required: false,
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '3' },
        ],
      },
      width: 4,
    },
    {
      id: 'field-owner3-role',
      name: 'owner3Role',
      label: 'Role',
      type: 'select',
      required: false,
      options: [
        { value: 'managing_member', label: 'Managing Member' },
        { value: 'member', label: 'Member' },
        { value: 'general_partner', label: 'General Partner' },
        { value: 'limited_partner', label: 'Limited Partner' },
        { value: 'trustee', label: 'Trustee' },
        { value: 'officer', label: 'Officer' },
        { value: 'shareholder', label: 'Shareholder' },
      ],
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '3' },
        ],
      },
      width: 4,
    },
    // Owner 4 fields
    {
      id: 'field-owner4-name',
      name: 'owner4Name',
      label: 'Owner 4 Name',
      type: 'text',
      required: false,
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '4' },
        ],
      },
      width: 4,
    },
    {
      id: 'field-owner4-percentage',
      name: 'owner4Percentage',
      label: 'Ownership %',
      type: 'number',
      required: false,
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '4' },
        ],
      },
      width: 4,
    },
    {
      id: 'field-owner4-role',
      name: 'owner4Role',
      label: 'Role',
      type: 'select',
      required: false,
      options: [
        { value: 'managing_member', label: 'Managing Member' },
        { value: 'member', label: 'Member' },
        { value: 'general_partner', label: 'General Partner' },
        { value: 'limited_partner', label: 'Limited Partner' },
        { value: 'trustee', label: 'Trustee' },
        { value: 'officer', label: 'Officer' },
        { value: 'shareholder', label: 'Shareholder' },
      ],
      conditionalDisplayGroup: {
        logic: 'AND',
        conditions: [
          { field: 'entityType', operator: 'not_equals', value: 'individual' },
          { field: 'numberOfOwners', operator: 'greater_than_or_equals', value: '4' },
        ],
      },
      width: 4,
    },
    {
      id: 'field-experience',
      name: 'flipsInLast2Years',
      label: 'Completed deals in last 24 months',
      type: 'select',
      required: true,
      options: [
        { value: '0', label: '0 - First time investor' },
        { value: '1-3', label: '1-3 deals' },
        { value: '4-10', label: '4-10 deals' },
        { value: '11+', label: '11+ deals' },
      ],
      helpText: 'Your track record helps us determine your rate',
      width: 6,
    },
    {
      id: 'field-has-guarantor',
      name: 'hasGuarantor',
      label: 'Will there be additional guarantors?',
      type: 'radio',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      width: 6,
    },
  ],
});

const createCoBorrowerStep = (): IntakeStepTemplate => ({
  id: 'step-co-borrower',
  order: 3,
  name: 'Co-Borrower Information',
  description: 'Add co-borrower details if applicable',
  icon: 'GroupAdd',
  requiresAuth: false,
  fields: [
    {
      id: 'field-has-co-borrower',
      name: 'hasCoBorrower',
      label: 'Will there be a co-borrower on this loan?',
      type: 'radio',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      width: 12,
    },
    // Co-Borrower 1
    {
      id: 'field-co-borrower1-entity-name',
      name: 'coBorrower1EntityName',
      label: 'CB 1 Entity Name',
      type: 'text',
      required: false,
      conditionalDisplay: { field: 'hasCoBorrower', operator: 'equals', value: 'yes' },
      width: 4,
    },
    {
      id: 'field-co-borrower1-role',
      name: 'coBorrower1Role',
      label: 'CB 1 Role',
      type: 'select',
      required: false,
      options: [
        { value: 'co_borrower', label: 'Co-Borrower' },
        { value: 'sponsor', label: 'Sponsor' },
      ],
      conditionalDisplay: { field: 'hasCoBorrower', operator: 'equals', value: 'yes' },
      width: 4,
    },
    {
      id: 'field-co-borrower1-ownership',
      name: 'coBorrower1OwnershipInterest',
      label: 'CB 1 Ownership %',
      type: 'number',
      required: false,
      conditionalDisplay: { field: 'hasCoBorrower', operator: 'equals', value: 'yes' },
      width: 4,
    },
    // Co-Borrower 2
    {
      id: 'field-co-borrower2-entity-name',
      name: 'coBorrower2EntityName',
      label: 'CB 2 Entity Name',
      type: 'text',
      required: false,
      conditionalDisplay: { field: 'hasCoBorrower', operator: 'equals', value: 'yes' },
      width: 4,
    },
    {
      id: 'field-co-borrower2-role',
      name: 'coBorrower2Role',
      label: 'CB 2 Role',
      type: 'select',
      required: false,
      options: [
        { value: 'co_borrower', label: 'Co-Borrower' },
        { value: 'sponsor', label: 'Sponsor' },
      ],
      conditionalDisplay: { field: 'hasCoBorrower', operator: 'equals', value: 'yes' },
      width: 4,
    },
    {
      id: 'field-co-borrower2-ownership',
      name: 'coBorrower2OwnershipInterest',
      label: 'CB 2 Ownership %',
      type: 'number',
      required: false,
      conditionalDisplay: { field: 'hasCoBorrower', operator: 'equals', value: 'yes' },
      width: 4,
    },
    // Co-Borrower 3
    {
      id: 'field-co-borrower3-entity-name',
      name: 'coBorrower3EntityName',
      label: 'CB 3 Entity Name',
      type: 'text',
      required: false,
      conditionalDisplay: { field: 'hasCoBorrower', operator: 'equals', value: 'yes' },
      width: 4,
    },
    {
      id: 'field-co-borrower3-role',
      name: 'coBorrower3Role',
      label: 'CB 3 Role',
      type: 'select',
      required: false,
      options: [
        { value: 'co_borrower', label: 'Co-Borrower' },
        { value: 'sponsor', label: 'Sponsor' },
      ],
      conditionalDisplay: { field: 'hasCoBorrower', operator: 'equals', value: 'yes' },
      width: 4,
    },
    {
      id: 'field-co-borrower3-ownership',
      name: 'coBorrower3OwnershipInterest',
      label: 'CB 3 Ownership %',
      type: 'number',
      required: false,
      conditionalDisplay: { field: 'hasCoBorrower', operator: 'equals', value: 'yes' },
      width: 4,
    },
  ],
});

const createPropertyAddressStep = (): IntakeStepTemplate => ({
  id: 'step-property-address',
  order: 4,
  name: 'Property Address',
  description: 'Enter the subject property address',
  icon: 'LocationOn',
  requiresAuth: false,
  fields: [
    {
      id: 'field-property-address',
      name: 'propertyAddress',
      label: 'Property Address',
      type: 'address',
      required: true,
      helpText: 'Enter the full address of the property',
      width: 12,
    },
    {
      id: 'field-property-type',
      name: 'propertyType',
      label: 'Property Type',
      type: 'select',
      required: true,
      options: [
        { value: 'sfr', label: 'Single Family Residence' },
        { value: 'condo', label: 'Condominium' },
        { value: 'townhouse', label: 'Townhouse' },
        { value: '2-4_unit', label: '2-4 Unit Property' },
        { value: 'multifamily', label: 'Multifamily (5+ units)' },
        { value: 'mixed_use', label: 'Mixed Use' },
        { value: 'commercial', label: 'Commercial' },
      ],
      width: 6,
    },
    {
      id: 'field-property-condition',
      name: 'propertyCondition',
      label: 'Current Property Condition',
      type: 'select',
      required: true,
      options: [
        { value: 'excellent', label: 'Excellent - Move-in ready' },
        { value: 'good', label: 'Good - Minor repairs needed' },
        { value: 'fair', label: 'Fair - Moderate renovation' },
        { value: 'poor', label: 'Poor - Major renovation' },
        { value: 'land', label: 'Vacant land' },
      ],
      width: 6,
    },
  ],
});

// =============================================================================
// BRIDGE / F&F / CONSTRUCTION — Enhanced Steps
// =============================================================================

/**
 * Enhanced property details for bridge/F&F/construction — includes physical
 * attributes (sqft, bed/bath/garage) that the basic PropertyAddress step lacks.
 */
const createPropertyDetailsStep = (): IntakeStepTemplate => ({
  id: 'step-property-details',
  order: 3,
  name: 'Property Details',
  description: 'Describe the subject property',
  icon: 'Home',
  requiresAuth: false,
  fields: [
    {
      id: 'field-property-address',
      name: 'propertyAddress',
      label: 'Property Address',
      type: 'address',
      required: true,
      helpText: 'Enter the full address of the property',
      width: 12,
    },
    {
      id: 'field-property-type',
      name: 'propertyType',
      label: 'Property Type',
      type: 'select',
      required: true,
      options: [
        { value: 'sfr', label: 'Single Family Residence' },
        { value: 'condo', label: 'Condominium' },
        { value: 'townhouse', label: 'Townhouse' },
        { value: '2-4_unit', label: '2-4 Unit Property' },
        { value: 'multifamily', label: 'Multifamily (5+ units)' },
        { value: 'mixed_use', label: 'Mixed Use' },
        { value: 'commercial', label: 'Commercial' },
      ],
      width: 6,
    },
    {
      id: 'field-property-condition',
      name: 'propertyCondition',
      label: 'Current Property Condition',
      type: 'select',
      required: true,
      options: [
        { value: 'excellent', label: 'Excellent - Move-in ready' },
        { value: 'good', label: 'Good - Minor repairs needed' },
        { value: 'fair', label: 'Fair - Moderate renovation' },
        { value: 'poor', label: 'Poor - Major renovation' },
        { value: 'land', label: 'Vacant land' },
      ],
      width: 6,
    },
    {
      id: 'field-sqft-living',
      name: 'propertySqFtLiving',
      label: 'Living Area (SF)',
      type: 'number',
      required: true,
      validation: { min: 100, max: 100000 },
      placeholder: 'e.g. 1800',
      helpText: 'Finished living square footage',
      width: 4,
    },
    {
      id: 'field-sqft-gross',
      name: 'propertySqFtGross',
      label: 'Gross Area (SF)',
      type: 'number',
      required: false,
      validation: { min: 100, max: 200000 },
      placeholder: 'e.g. 2200',
      helpText: 'Total building square footage including garage/unfinished',
      width: 4,
    },
    {
      id: 'field-year-built',
      name: 'yearBuilt',
      label: 'Year Built',
      type: 'number',
      required: false,
      validation: { min: 1800, max: 2030 },
      placeholder: 'e.g. 1985',
      width: 4,
    },
    {
      id: 'field-bedrooms',
      name: 'bedrooms',
      label: 'Bedrooms',
      type: 'select',
      required: true,
      options: [
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
        { value: '5', label: '5' },
        { value: '6+', label: '6+' },
      ],
      width: 4,
    },
    {
      id: 'field-bathrooms',
      name: 'bathrooms',
      label: 'Bathrooms',
      type: 'select',
      required: true,
      options: [
        { value: '1', label: '1' },
        { value: '1.5', label: '1.5' },
        { value: '2', label: '2' },
        { value: '2.5', label: '2.5' },
        { value: '3', label: '3' },
        { value: '3.5', label: '3.5' },
        { value: '4+', label: '4+' },
      ],
      width: 4,
    },
    {
      id: 'field-garage-count',
      name: 'garageCount',
      label: 'Garage',
      type: 'select',
      required: false,
      options: [
        { value: '0', label: 'None' },
        { value: '1', label: '1 Car' },
        { value: '2', label: '2 Car' },
        { value: '3+', label: '3+ Car' },
      ],
      width: 4,
    },
    {
      id: 'field-occupancy-status',
      name: 'occupancyStatus',
      label: 'Occupancy Status',
      type: 'select',
      required: true,
      options: [
        { value: 'vacant', label: 'Vacant' },
        { value: 'owner_occupied', label: 'Owner-Occupied' },
        { value: 'tenant_occupied', label: 'Tenant-Occupied' },
      ],
      helpText: 'Current occupancy affects loan terms',
      width: 6,
    },
  ],
  sections: [
    {
      id: 'section-property-address',
      title: 'Property Address',
      fieldIds: ['field-property-address', 'field-property-type', 'field-property-condition'],
    },
    {
      id: 'section-physical-details',
      title: 'Physical Details',
      fieldIds: [
        'field-sqft-living',
        'field-sqft-gross',
        'field-year-built',
        'field-bedrooms',
        'field-bathrooms',
        'field-garage-count',
        'field-occupancy-status',
      ],
    },
  ],
});

/**
 * Deal financials — purchase/rehab costs, ARV, existing liens, exit strategy.
 * Used by Bridge, F&F, and Construction templates.
 */
const createDealFinancialsStep = (): IntakeStepTemplate => ({
  id: 'step-deal-financials',
  order: 4,
  name: 'Deal Financials',
  description: 'Provide the financial details of the deal',
  icon: 'AttachMoney',
  requiresAuth: false,
  fields: [
    {
      id: 'field-purchase-price',
      name: 'purchasePrice',
      label: 'Property Purchase Price',
      type: 'currency',
      required: true,
      validation: { min: 25000, max: 50000000 },
      placeholder: '$0',
      width: 6,
    },
    {
      id: 'field-purchase-date',
      name: 'purchaseDate',
      label: 'Purchase Date',
      type: 'date',
      required: false,
      helpText: 'Date property was or will be purchased',
      width: 6,
    },
    {
      id: 'field-rehab-cost-spent',
      name: 'rehabCostSpentToDate',
      label: 'Build/Renovation Cost Spent to Date',
      type: 'currency',
      required: false,
      validation: { min: 0, max: 10000000 },
      placeholder: '$0',
      helpText: 'How much has been spent on renovations so far',
      width: 6,
    },
    {
      id: 'field-remaining-cost',
      name: 'remainingCostToComplete',
      label: 'Remaining Cost to Complete',
      type: 'currency',
      required: false,
      validation: { min: 0, max: 10000000 },
      placeholder: '$0',
      helpText: 'Estimated cost to finish all planned work',
      width: 6,
    },
    {
      id: 'field-arv',
      name: 'afterRepairValue',
      label: 'Expected Sales Price / ARV',
      type: 'currency',
      required: true,
      validation: { min: 50000, max: 100000000 },
      placeholder: '$0',
      helpText: 'After-repair value or expected sale price',
      width: 6,
    },
    {
      id: 'field-existing-loan-payoff',
      name: 'existingLoanPayoff',
      label: 'Existing Loan Payoff',
      type: 'currency',
      required: false,
      validation: { min: 0, max: 50000000 },
      placeholder: '$0',
      helpText: 'Current balance on any existing loans on the property',
      width: 6,
    },
    {
      id: 'field-loan-amount',
      name: 'requestedLoanAmount',
      label: 'Requested Loan Amount',
      type: 'currency',
      required: true,
      validation: { min: 50000, max: 50000000 },
      placeholder: '$0',
      width: 6,
    },
    {
      id: 'field-exit-strategy',
      name: 'exitStrategy',
      label: 'Exit Plan',
      type: 'textarea',
      required: true,
      placeholder: 'e.g. Renovate and sell within 12 months, refinance into DSCR loan, etc.',
      helpText: 'Describe how you plan to repay or exit the loan',
      width: 12,
    },
    {
      id: 'field-exit-timeline',
      name: 'exitTimeline',
      label: 'Expected Exit Timeline',
      type: 'select',
      required: true,
      options: [
        { value: '3_months', label: '0-3 months' },
        { value: '6_months', label: '3-6 months' },
        { value: '12_months', label: '6-12 months' },
        { value: '18_months', label: '12-18 months' },
        { value: '24_months', label: '18-24 months' },
      ],
      width: 6,
    },
    {
      id: 'field-term',
      name: 'preferredTerm',
      label: 'Preferred Loan Term',
      type: 'select',
      required: true,
      defaultValue: '12',
      options: [
        { value: '6', label: '6 months' },
        { value: '9', label: '9 months' },
        { value: '12', label: '12 months' },
        { value: '18', label: '18 months' },
        { value: '24', label: '24 months' },
      ],
      width: 6,
    },
  ],
  sections: [
    {
      id: 'section-acquisition',
      title: 'Acquisition',
      fieldIds: ['field-purchase-price', 'field-purchase-date'],
    },
    {
      id: 'section-renovation',
      title: 'Renovation / Construction Costs',
      fieldIds: ['field-rehab-cost-spent', 'field-remaining-cost'],
    },
    {
      id: 'section-valuation-loan',
      title: 'Valuation & Loan',
      fieldIds: ['field-arv', 'field-existing-loan-payoff', 'field-loan-amount'],
    },
    {
      id: 'section-exit',
      title: 'Exit Strategy & Term',
      fieldIds: ['field-exit-strategy', 'field-exit-timeline', 'field-term'],
    },
  ],
});

/**
 * Borrower financial summary — upfront (not auth-gated).
 * Lenders need credit, liquidity, and net worth to size a deal.
 */
const createBorrowerFinancialsStep = (): IntakeStepTemplate => ({
  id: 'step-borrower-financials',
  order: 5,
  name: 'Borrower Information',
  description: 'Key financial details for deal sizing',
  icon: 'Person',
  requiresAuth: false,
  fields: [
    {
      id: 'field-entity-or-principal',
      name: 'entityOrPrincipalName',
      label: 'Entity or Principal Name',
      type: 'text',
      required: true,
      placeholder: 'e.g. ABC Holdings LLC or John Smith',
      helpText: 'The borrowing entity name or individual borrower',
      width: 12,
    },
    {
      id: 'field-credit-score',
      name: 'estimatedFico',
      label: 'Credit Score',
      type: 'select',
      required: true,
      options: [
        { value: '760+', label: '760+' },
        { value: '740-759', label: '740-759' },
        { value: '720-739', label: '720-739' },
        { value: '700-719', label: '700-719' },
        { value: '680-699', label: '680-699' },
        { value: '660-679', label: '660-679' },
        { value: 'below-660', label: 'Below 660' },
      ],
      width: 6,
    },
    {
      id: 'field-borrower-liquidity',
      name: 'borrowerLiquidity',
      label: 'Borrower Liquidity',
      type: 'currency',
      required: true,
      validation: { min: 0, max: 100000000 },
      placeholder: '$0',
      helpText: 'Total cash and liquid assets (checking, savings, stocks, etc.)',
      width: 6,
    },
    {
      id: 'field-borrower-net-worth',
      name: 'borrowerNetWorth',
      label: 'Borrower Net Worth',
      type: 'currency',
      required: true,
      validation: { min: 0, max: 1000000000 },
      placeholder: '$0',
      helpText: 'Total assets minus total liabilities',
      width: 6,
    },
    {
      id: 'field-exits-refis-36mo',
      name: 'exitsOrRefisInPast36Months',
      label: 'Exits or Refis in Past 36 Months',
      type: 'select',
      required: true,
      options: [
        { value: '0', label: '0' },
        { value: '1-2', label: '1-2' },
        { value: '3-5', label: '3-5' },
        { value: '6-10', label: '6-10' },
        { value: '11+', label: '11+' },
      ],
      helpText: 'Number of investment property transactions completed',
      width: 6,
    },
  ],
});

/**
 * Permit status — shown for F&F and Construction.
 * For pure bridge loans, conditionally hidden via investmentType check.
 */
const createPermitStatusStep = (): IntakeStepTemplate => ({
  id: 'step-permit-status',
  order: 6,
  name: 'Permit Status',
  description: 'Current permitting status for the project',
  icon: 'Assignment',
  requiresAuth: false,
  fields: [
    {
      id: 'field-permit-status',
      name: 'permitStatus',
      label: 'Permit Status',
      type: 'select',
      required: true,
      options: [
        { value: 'in_hand', label: 'In Hand — Permits approved' },
        { value: 'applied', label: 'Applied — Waiting for approval' },
        { value: 'not_yet', label: "Not Yet — Haven't applied" },
        { value: 'not_applicable', label: 'N/A — No permits needed' },
      ],
      width: 6,
    },
    {
      id: 'field-permit-timeline',
      name: 'permitTimeline',
      label: 'Expected Timeline to Obtain Permits',
      type: 'text',
      required: false,
      placeholder: 'e.g. 4-6 weeks',
      conditionalDisplayGroup: {
        logic: 'OR',
        conditions: [
          { field: 'permitStatus', operator: 'equals', value: 'applied' },
          { field: 'permitStatus', operator: 'equals', value: 'not_yet' },
        ],
      },
      width: 6,
    },
  ],
});

/**
 * Builder / GC details — Construction template only.
 */
const createBuilderDetailsStep = (): IntakeStepTemplate => ({
  id: 'step-builder-details',
  order: 7,
  name: 'Builder & GC Details',
  description: 'Construction experience and general contractor information',
  icon: 'Engineering',
  requiresAuth: false,
  fields: [
    {
      id: 'field-builder-completions',
      name: 'builderCompletions36Months',
      label: 'Builder Completions in Past 36 Months',
      type: 'select',
      required: true,
      options: [
        { value: '0', label: '0 — First build' },
        { value: '1-2', label: '1-2' },
        { value: '3-5', label: '3-5' },
        { value: '6-10', label: '6-10' },
        { value: '11+', label: '11+' },
      ],
      helpText: 'Number of ground-up construction projects completed',
      width: 6,
    },
    {
      id: 'field-gc-name',
      name: 'generalContractorName',
      label: 'General Contractor Name',
      type: 'text',
      required: false,
      placeholder: 'Name of licensed GC',
      width: 6,
    },
    {
      id: 'field-gc-license',
      name: 'generalContractorLicense',
      label: 'GC License Number',
      type: 'text',
      required: false,
      placeholder: 'State license #',
      width: 6,
    },
  ],
});

/**
 * Certificate of Occupancy — Bridge loan only.
 */
const createCertificateOfOccupancyStep = (): IntakeStepTemplate => ({
  id: 'step-certificate-of-occupancy',
  order: 6,
  name: 'Certificate of Occupancy',
  description: 'CO status for the property',
  icon: 'VerifiedUser',
  requiresAuth: false,
  fields: [
    {
      id: 'field-co-status',
      name: 'certificateOfOccupancy',
      label: 'Is the Certificate of Occupancy in hand?',
      type: 'select',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'pending', label: 'Pending' },
      ],
      width: 6,
    },
    {
      id: 'field-co-expected-date',
      name: 'coExpectedDate',
      label: 'Expected CO Date',
      type: 'date',
      required: false,
      conditionalDisplayGroup: {
        logic: 'OR',
        conditions: [
          { field: 'certificateOfOccupancy', operator: 'equals', value: 'no' },
          { field: 'certificateOfOccupancy', operator: 'equals', value: 'pending' },
        ],
      },
      width: 6,
    },
  ],
});

const createRateCalculatorStep = (config: {
  maxLTV: number;
  maxLTC: number;
  defaultRate: number;
  minLoanAmount: number;
  maxLoanAmount: number;
}): IntakeStepTemplate => ({
  id: 'step-rate-calculator',
  order: 5,
  name: 'Rate Calculator',
  description: 'Calculate your estimated rate and terms',
  icon: 'Calculate',
  requiresAuth: false,
  fields: [
    {
      id: 'field-purchase-price',
      name: 'purchasePrice',
      label: 'Purchase Price',
      type: 'currency',
      required: true,
      validation: { min: 50000, max: 50000000 },
      placeholder: '$0',
      width: 6,
    },
    {
      id: 'field-rehab-budget',
      name: 'rehabBudget',
      label: 'Rehab Budget',
      type: 'currency',
      required: false,
      validation: { min: 0, max: 5000000 },
      placeholder: '$0',
      helpText: 'Estimated renovation costs',
      width: 6,
    },
    {
      id: 'field-arv',
      name: 'afterRepairValue',
      label: 'After Repair Value (ARV)',
      type: 'currency',
      required: true,
      validation: { min: 50000, max: 100000000 },
      placeholder: '$0',
      helpText: 'Estimated value after improvements',
      width: 6,
    },
    {
      id: 'field-loan-amount',
      name: 'requestedLoanAmount',
      label: 'Requested Loan Amount',
      type: 'currency',
      required: true,
      validation: { min: config.minLoanAmount, max: config.maxLoanAmount },
      placeholder: '$0',
      width: 6,
    },
    {
      id: 'field-fico',
      name: 'estimatedFico',
      label: 'Estimated Credit Score',
      type: 'select',
      required: true,
      options: [
        { value: '760+', label: '760+' },
        { value: '740-759', label: '740-759' },
        { value: '720-739', label: '720-739' },
        { value: '700-719', label: '700-719' },
        { value: '680-699', label: '680-699' },
        { value: '660-679', label: '660-679' },
        { value: 'below-660', label: 'Below 660' },
      ],
      width: 6,
    },
    {
      id: 'field-term',
      name: 'preferredTerm',
      label: 'Preferred Loan Term',
      type: 'select',
      required: true,
      defaultValue: '12',
      options: [
        { value: '6', label: '6 months' },
        { value: '9', label: '9 months' },
        { value: '12', label: '12 months' },
        { value: '18', label: '18 months' },
        { value: '24', label: '24 months' },
      ],
      width: 6,
    },
  ],
});

const createComplianceStep = (questions: IntakeComplianceQuestion[]): IntakeStepTemplate => ({
  id: 'step-compliance',
  order: 6,
  name: 'Compliance',
  description: 'Answer required compliance questions',
  icon: 'Verified',
  requiresAuth: false,
  fields: questions.map((q, index) => ({
    id: `field-compliance-${q.id}`,
    name: `compliance_${q.id}`,
    label: q.question,
    type: 'radio' as const,
    required: true,
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
    ],
    width: 12,
  })),
});

const createSigningDateStep = (): IntakeStepTemplate => ({
  id: 'step-signing-date',
  order: 6,
  name: 'Signing Date',
  description: 'Select your preferred closing date',
  icon: 'Event',
  requiresAuth: true, // AUTH CHECKPOINT
  fields: [
    {
      id: 'field-signing-date',
      name: 'preferredSigningDate',
      label: 'Preferred Signing Date',
      type: 'date',
      required: true,
      validation: { min: 7, max: 90 }, // Days from now
      helpText: 'Select a date between 7-90 days from now',
      width: 6,
    },
    {
      id: 'field-signing-flexibility',
      name: 'signingFlexibility',
      label: 'Date Flexibility',
      type: 'select',
      required: true,
      options: [
        { value: 'exact', label: 'Exact date required' },
        { value: 'flexible_1_week', label: 'Flexible +/- 1 week' },
        { value: 'flexible_2_weeks', label: 'Flexible +/- 2 weeks' },
        { value: 'no_rush', label: 'No rush - whenever ready' },
      ],
      width: 6,
    },
    {
      id: 'field-additional-notes',
      name: 'additionalNotes',
      label: 'Additional Notes',
      type: 'text',
      required: false,
      placeholder: 'Any special requirements or notes for your loan officer',
      width: 12,
    },
  ],
});

const createGuarantorDetailsStep = (): IntakeStepTemplate => ({
  id: 'step-guarantor-details',
  order: 7,
  name: 'Guarantor Details',
  description: 'Enter guarantor information',
  icon: 'Person',
  requiresAuth: true,
  fields: [
    {
      id: 'field-number-of-guarantors',
      name: 'numberOfGuarantors',
      label: 'Number of Guarantors',
      type: 'select',
      required: true,
      options: [
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
      ],
      width: 6,
    },
    // Guarantor 1 fields (always shown)
    {
      id: 'field-guarantor-first-name',
      name: 'guarantorFirstName',
      label: 'First Name',
      type: 'text',
      required: true,
      width: 6,
    },
    {
      id: 'field-guarantor-last-name',
      name: 'guarantorLastName',
      label: 'Last Name',
      type: 'text',
      required: true,
      width: 6,
    },
    {
      id: 'field-guarantor-dob',
      name: 'guarantorDob',
      label: 'Date of Birth',
      type: 'date',
      required: true,
      helpText: 'Must be 18 years or older',
      width: 6,
    },
    {
      id: 'field-guarantor-phone',
      name: 'guarantorPhone',
      label: 'Phone Number',
      type: 'phone',
      required: true,
      width: 6,
    },
    {
      id: 'field-guarantor-email',
      name: 'guarantorEmail',
      label: 'Email Address',
      type: 'email',
      required: true,
      width: 6,
    },
    {
      id: 'field-guarantor-address',
      name: 'guarantorAddress',
      label: 'Home Address',
      type: 'address',
      required: true,
      width: 12,
    },
    {
      id: 'field-guarantor1-guarantee-type',
      name: 'guarantor1GuaranteeType',
      label: 'Guarantee Type',
      type: 'select',
      required: false,
      options: [
        { value: 'full_recourse', label: 'Full Recourse' },
        { value: 'partial_recourse', label: 'Partial Recourse' },
        { value: 'payment', label: 'Payment Guarantee' },
        { value: 'completion', label: 'Completion Guarantee' },
        { value: 'carve_out', label: 'Carve-Out' },
      ],
      width: 4,
    },
    {
      id: 'field-guarantor1-net-worth',
      name: 'guarantor1NetWorth',
      label: 'Net Worth',
      type: 'currency',
      required: false,
      width: 4,
    },
    {
      id: 'field-guarantor1-liquidity',
      name: 'guarantor1Liquidity',
      label: 'Liquidity',
      type: 'currency',
      required: false,
      width: 4,
    },
    // Guarantor 2 fields
    {
      id: 'field-guarantor2-first-name',
      name: 'guarantor2FirstName',
      label: 'Guarantor 2 First Name',
      type: 'text',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '2' },
      width: 6,
    },
    {
      id: 'field-guarantor2-last-name',
      name: 'guarantor2LastName',
      label: 'Guarantor 2 Last Name',
      type: 'text',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '2' },
      width: 6,
    },
    {
      id: 'field-guarantor2-email',
      name: 'guarantor2Email',
      label: 'Guarantor 2 Email',
      type: 'email',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '2' },
      width: 6,
    },
    {
      id: 'field-guarantor2-phone',
      name: 'guarantor2Phone',
      label: 'Guarantor 2 Phone',
      type: 'phone',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '2' },
      width: 6,
    },
    {
      id: 'field-guarantor2-guarantee-type',
      name: 'guarantor2GuaranteeType',
      label: 'Guarantee Type',
      type: 'select',
      required: false,
      options: [
        { value: 'full_recourse', label: 'Full Recourse' },
        { value: 'partial_recourse', label: 'Partial Recourse' },
        { value: 'payment', label: 'Payment Guarantee' },
        { value: 'completion', label: 'Completion Guarantee' },
        { value: 'carve_out', label: 'Carve-Out' },
      ],
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '2' },
      width: 4,
    },
    {
      id: 'field-guarantor2-net-worth',
      name: 'guarantor2NetWorth',
      label: 'Net Worth',
      type: 'currency',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '2' },
      width: 4,
    },
    {
      id: 'field-guarantor2-liquidity',
      name: 'guarantor2Liquidity',
      label: 'Liquidity',
      type: 'currency',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '2' },
      width: 4,
    },
    // Guarantor 3 fields
    {
      id: 'field-guarantor3-first-name',
      name: 'guarantor3FirstName',
      label: 'Guarantor 3 First Name',
      type: 'text',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '3' },
      width: 6,
    },
    {
      id: 'field-guarantor3-last-name',
      name: 'guarantor3LastName',
      label: 'Guarantor 3 Last Name',
      type: 'text',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '3' },
      width: 6,
    },
    {
      id: 'field-guarantor3-email',
      name: 'guarantor3Email',
      label: 'Guarantor 3 Email',
      type: 'email',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '3' },
      width: 6,
    },
    {
      id: 'field-guarantor3-phone',
      name: 'guarantor3Phone',
      label: 'Guarantor 3 Phone',
      type: 'phone',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '3' },
      width: 6,
    },
    // Guarantor 4 fields
    {
      id: 'field-guarantor4-first-name',
      name: 'guarantor4FirstName',
      label: 'Guarantor 4 First Name',
      type: 'text',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '4' },
      width: 6,
    },
    {
      id: 'field-guarantor4-last-name',
      name: 'guarantor4LastName',
      label: 'Guarantor 4 Last Name',
      type: 'text',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '4' },
      width: 6,
    },
    {
      id: 'field-guarantor4-email',
      name: 'guarantor4Email',
      label: 'Guarantor 4 Email',
      type: 'email',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '4' },
      width: 6,
    },
    {
      id: 'field-guarantor4-phone',
      name: 'guarantor4Phone',
      label: 'Guarantor 4 Phone',
      type: 'phone',
      required: false,
      conditionalDisplay: { field: 'numberOfGuarantors', operator: 'greater_than_or_equals', value: '4' },
      width: 6,
    },
  ],
});

const createSsnVerificationStep = (): IntakeStepTemplate => ({
  id: 'step-ssn-verification',
  order: 8,
  name: 'SSN Verification',
  description: 'Provide SSN for credit authorization',
  icon: 'Security',
  requiresAuth: true,
  fields: [
    {
      id: 'field-ssn',
      name: 'ssn',
      label: 'Social Security Number',
      type: 'ssn',
      required: true,
      sensitive: true,
      helpText: 'Required for credit check. Your SSN is encrypted and secure.',
      width: 6,
    },
    {
      id: 'field-credit-auth',
      name: 'creditAuthConsent',
      label: 'I authorize a credit check for the purpose of this loan application',
      type: 'checkbox',
      required: true,
      width: 12,
    },
    {
      id: 'field-terms-consent',
      name: 'termsConsent',
      label: 'I have read and agree to the Terms of Service and Privacy Policy',
      type: 'checkbox',
      required: true,
      width: 12,
    },
  ],
});

// =============================================================================
// SHARED COMPLIANCE QUESTIONS
// =============================================================================

const STANDARD_COMPLIANCE_QUESTIONS: IntakeComplianceQuestion[] = [
  {
    id: 'us-citizen',
    question: 'Are you or your entity authorized to do business in the United States?',
    requiredAnswer: true,
    blockingMessage: 'We currently only serve US-based borrowers.',
    linkedField: 'usCitizen',
    linkedFieldAcceptedValues: ['yes', 'permanent_resident'],
  },
  {
    id: 'non-owner-occupied',
    question: 'Will this be a non-owner occupied investment property?',
    requiredAnswer: true,
    blockingMessage: 'This loan program is only for investment properties.',
  },
  {
    id: 'no-bankruptcy',
    question: 'Have you been free of bankruptcy for at least 2 years?',
    requiredAnswer: true,
    blockingMessage: 'Recent bankruptcy may affect your eligibility.',
  },
  {
    id: 'no-foreclosure',
    question: 'Have you been free of foreclosure for at least 3 years?',
    requiredAnswer: true,
    blockingMessage: 'Recent foreclosure may affect your eligibility.',
  },
];

// =============================================================================
// BRIDGE / FIX & FLIP TEMPLATE
// =============================================================================

export const BRIDGE_FIX_FLIP_TEMPLATE: IntakeFlowTemplate = {
  id: INTAKE_FLOW_TEMPLATE_IDS.BRIDGE_FIX_FLIP,
  name: 'Bridge / Fix & Flip Loan',
  description: 'Short-term financing for property acquisition and renovation projects.',
  category: 'short-term',
  icon: 'Construction',
  color: '#FF6B35',
  tags: ['bridge', 'fix-flip', 'renovation', 'short-term', 'investment'],
  estimatedDuration: '5-10 minutes',
  complexity: 'intermediate',
  industryFit: ['real_estate', 'lending'],
  data: {
    intakeType: 'bridge',
    version: '2.0.0',
    displayMode: IntakeDisplayMode.SCROLL,
    steps: [
      createInvestmentTypeStep(),
      createContactInfoStep(),
      createPropertyDetailsStep(),
      createDealFinancialsStep(),
      createPermitStatusStep(), // Shown for F&F; bridge borrowers can select N/A
      createCertificateOfOccupancyStep(), // Primarily for bridge (post-construction)
      createBorrowerFinancialsStep(),
      createEntityExperienceStep(),
      createCoBorrowerStep(),
      createRateCalculatorStep({
        maxLTV: 75,
        maxLTC: 90,
        defaultRate: 10.5,
        minLoanAmount: 75000,
        maxLoanAmount: 3000000,
      }),
      // Compliance questions are rendered as checkboxes in the bottom "Terms & Compliance"
      // section via complianceQuestions property. Do NOT also include as a step here —
      // the step renders radios (string 'yes'/'no') while the bottom section uses
      // checkboxes (boolean true/false), causing conflicting form state on the same keys.
      createSigningDateStep(),
      createGuarantorDetailsStep(),
      createSsnVerificationStep(),
    ].map((step, index) => ({ ...step, order: index })),
    rateCalculatorConfig: {
      maxLTV: 75,
      maxLTC: 90,
      termOptions: [6, 9, 12, 18, 24],
      defaultRate: 10.5,
      minLoanAmount: 75000,
      maxLoanAmount: 3000000,
      rateAdjustments: [
        { condition: 'fico >= 760', adjustment: -0.5, description: 'Excellent credit discount' },
        { condition: 'experience >= 5', adjustment: -0.25, description: 'Experienced investor discount' },
        { condition: 'ltv <= 65', adjustment: -0.25, description: 'Low LTV discount' },
      ],
    },
    complianceQuestions: STANDARD_COMPLIANCE_QUESTIONS,
    exitBehavior: 'case_creation',
  },
};

// =============================================================================
// CONSTRUCTION TEMPLATE
// =============================================================================

export const CONSTRUCTION_TEMPLATE: IntakeFlowTemplate = {
  id: INTAKE_FLOW_TEMPLATE_IDS.CONSTRUCTION,
  name: 'Ground-Up Construction Loan',
  description: 'Financing for new construction projects from the ground up.',
  category: 'development',
  icon: 'Engineering',
  color: '#4CAF50',
  tags: ['construction', 'ground-up', 'development', 'new-build'],
  estimatedDuration: '10-15 minutes',
  complexity: 'advanced',
  industryFit: ['real_estate', 'lending'],
  data: {
    intakeType: 'construction',
    version: '2.0.0',
    displayMode: IntakeDisplayMode.SCROLL,
    steps: [
      createInvestmentTypeStep(),
      createContactInfoStep(),
      createPropertyDetailsStep(),
      createDealFinancialsStep(),
      createPermitStatusStep(),
      createBuilderDetailsStep(),
      createBorrowerFinancialsStep(),
      createEntityExperienceStep(),
      createCoBorrowerStep(),
      createRateCalculatorStep({
        maxLTV: 70,
        maxLTC: 85,
        defaultRate: 11.5,
        minLoanAmount: 150000,
        maxLoanAmount: 10000000,
      }),
      // Compliance handled via complianceQuestions property (bottom checkbox section)
      createSigningDateStep(),
      createGuarantorDetailsStep(),
      createSsnVerificationStep(),
    ].map((step, index) => ({ ...step, order: index })),
    rateCalculatorConfig: {
      maxLTV: 70,
      maxLTC: 85,
      termOptions: [12, 18, 24],
      defaultRate: 11.5,
      minLoanAmount: 150000,
      maxLoanAmount: 10000000,
    },
    complianceQuestions: [
      ...STANDARD_COMPLIANCE_QUESTIONS,
      {
        id: 'permits-ready',
        question: 'Do you have or expect to obtain all necessary building permits?',
        requiredAnswer: true,
        blockingMessage: 'Building permits are required for construction financing.',
      },
    ],
    exitBehavior: 'case_creation',
  },
};

// =============================================================================
// DSCR-SPECIFIC STEP FACTORIES
// =============================================================================

const createDscrContactInfoStep = (): IntakeStepTemplate => ({
  ...createContactInfoStep(),
  fields: [
    ...createContactInfoStep().fields,
    {
      id: 'field-us-citizen',
      name: 'usCitizen',
      label: 'U.S. Citizenship Status',
      type: 'radio',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'permanent_resident', label: 'Permanent Resident' },
      ],
      width: 6,
    },
    {
      id: 'field-at-least-21',
      name: 'atLeast21',
      label: 'Are you at least 21 years old?',
      type: 'radio',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      width: 6,
    },
  ],
});

const createDscrLoanStructureStep = (): IntakeStepTemplate => ({
  id: 'step-dscr-loan-structure',
  order: 2,
  name: 'Loan Structure',
  description: 'Define your loan purpose and collateral structure',
  icon: 'AccountTree',
  requiresAuth: false,
  fields: [
    {
      id: 'field-loan-purpose',
      name: 'loanPurpose',
      label: 'Loan Purpose',
      type: 'radio',
      required: true,
      options: [
        { value: 'purchase', label: 'Purchase' },
        { value: 'refinance', label: 'Refinance' },
      ],
      width: 12,
    },
    {
      id: 'field-cross-collateral',
      name: 'crossCollateral',
      label: 'Cross-Collateral Loan?',
      type: 'radio',
      required: true,
      options: [
        { value: 'yes', label: 'Yes (Multiple Properties)' },
        { value: 'no', label: 'No (Single Property)' },
      ],
      helpText: 'Cross-collateral loans use multiple properties as collateral',
      width: 12,
    },
  ],
});

const createDscrPropertyDetailsStep = (): IntakeStepTemplate => {
  const baseStep = createPropertyAddressStep();
  return {
    ...baseStep,
    name: 'Property Details',
    description: 'Enter property details for DSCR underwriting',
    fields: [
      // Keep address field
      ...baseStep.fields.filter((f) => f.name === 'propertyAddress'),
      // Expanded property type for DSCR
      {
        id: 'field-property-type',
        name: 'propertyType',
        label: 'Property Type',
        type: 'select',
        required: true,
        options: [
          { value: 'sfr', label: 'Single Family Residence' },
          { value: 'condo', label: 'Condominium' },
          { value: 'townhouse', label: 'Townhouse' },
          { value: '2-4_unit', label: '2-4 Unit Property' },
          { value: 'multifamily', label: 'Multifamily (5+ units)' },
          { value: 'mixed_use', label: 'Mixed Use' },
          { value: 'condotel', label: 'Condotel' },
          { value: 'pud', label: 'PUD' },
          { value: 'cooperative', label: 'Cooperative' },
          { value: 'modular_home', label: 'Modular Home' },
          { value: 'manufactured_home', label: 'Manufactured Home' },
        ],
        width: 6,
      },
      // C-rating condition scale
      {
        id: 'field-property-condition',
        name: 'propertyCondition',
        label: 'Property Condition Rating',
        type: 'select',
        required: true,
        options: [
          { value: 'c1', label: 'C1 - New Construction' },
          { value: 'c2', label: 'C2 - Excellent' },
          { value: 'c3', label: 'C3 - Good' },
          { value: 'c4', label: 'C4 - Average' },
          { value: 'c5', label: 'C5 - Fair' },
          { value: 'c6', label: 'C6 - Poor' },
        ],
        width: 6,
      },
      {
        id: 'field-county',
        name: 'county',
        label: 'County',
        type: 'text',
        required: false,
        helpText: 'County where the property is located',
        width: 6,
      },
      // --- Refinance Details (conditional on loanPurpose === 'refinance') ---
      {
        id: 'field-refinance-type',
        name: 'refinanceType',
        label: 'Refinance Type',
        type: 'select',
        required: false,
        options: [
          { value: 'cash_out', label: 'Cash-Out' },
          { value: 'rate_and_term', label: 'Rate & Term' },
          { value: 'delayed_purchase', label: 'Delayed Purchase' },
        ],
        conditionalDisplay: { field: 'loanPurpose', operator: 'equals', value: 'refinance' },
        width: 6,
      },
      {
        id: 'field-purchased-within-6-months',
        name: 'purchasedWithin6Months',
        label: 'Was the property purchased within the last 6 months?',
        type: 'radio',
        required: false,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Seasoning requirement for refinance eligibility',
        conditionalDisplay: { field: 'loanPurpose', operator: 'equals', value: 'refinance' },
        width: 6,
      },
      {
        id: 'field-existing-mortgage',
        name: 'existingMortgage',
        label: 'Is there an existing mortgage on this property?',
        type: 'radio',
        required: false,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        conditionalDisplay: { field: 'loanPurpose', operator: 'equals', value: 'refinance' },
        width: 6,
      },
      {
        id: 'field-mortgage-payoff-amount',
        name: 'mortgagePayoffAmount',
        label: 'Mortgage Payoff Amount',
        type: 'currency',
        required: false,
        placeholder: '$0',
        conditionalDisplayGroup: {
          logic: 'AND',
          conditions: [
            { field: 'loanPurpose', operator: 'equals', value: 'refinance' },
            { field: 'existingMortgage', operator: 'equals', value: 'yes' },
          ],
        },
        width: 6,
      },
      {
        id: 'field-paying-off-fix-flip-loan',
        name: 'payingOffFixFlipLoan',
        label: 'Are you paying off a fix & flip loan?',
        type: 'radio',
        required: false,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        conditionalDisplay: { field: 'loanPurpose', operator: 'equals', value: 'refinance' },
        width: 6,
      },
      // --- Property Risk Flags ---
      {
        id: 'field-is-rural',
        name: 'isRural',
        label: 'Is this a rural property?',
        type: 'radio',
        required: true,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        width: 4,
      },
      {
        id: 'field-is-declining-market',
        name: 'isDecliningMarket',
        label: 'Is this property in a declining market?',
        type: 'radio',
        required: true,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        width: 4,
      },
      {
        id: 'field-interest-only',
        name: 'interestOnly',
        label: 'Interest-only loan requested?',
        type: 'radio',
        required: true,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        width: 4,
      },
      // --- Physical Details ---
      {
        id: 'field-number-of-units',
        name: 'numberOfUnits',
        label: 'Number of Units',
        type: 'select',
        required: true,
        options: [
          { value: '1', label: '1' },
          { value: '2', label: '2' },
          { value: '3', label: '3' },
          { value: '4', label: '4' },
          { value: '5', label: '5' },
          { value: '6', label: '6' },
          { value: '7', label: '7' },
          { value: '8', label: '8' },
          { value: '9', label: '9' },
          { value: '10', label: '10+' },
        ],
        width: 6,
      },
      {
        id: 'field-market-value',
        name: 'marketValue',
        label: 'Estimated Current Market Value',
        type: 'currency',
        required: true,
        validation: { min: 50000, max: 50000000 },
        placeholder: '$0',
        width: 6,
      },
      {
        id: 'field-property-sqft',
        name: 'propertySqFt',
        label: 'Property Square Footage',
        type: 'number',
        required: false,
        validation: { min: 100, max: 100000 },
        width: 6,
      },
      {
        id: 'field-lot-size-acres',
        name: 'lotSizeAcres',
        label: 'Lot Size (Acres)',
        type: 'number',
        required: false,
        validation: { min: 0, max: 1000 },
        width: 6,
      },
    ],
    sections: [
      {
        id: 'section-property-address',
        title: 'Property Address',
        fieldIds: ['field-property-address', 'field-property-type', 'field-property-condition', 'field-county'],
      },
      {
        id: 'section-refinance-details',
        title: 'Refinance Details',
        conditionalDisplay: { field: 'loanPurpose', operator: 'equals', value: 'refinance' },
        fieldIds: [
          'field-refinance-type',
          'field-purchased-within-6-months',
          'field-existing-mortgage',
          'field-mortgage-payoff-amount',
          'field-paying-off-fix-flip-loan',
        ],
      },
      {
        id: 'section-property-risk-flags',
        title: 'Property Risk Flags',
        fieldIds: ['field-is-rural', 'field-is-declining-market', 'field-interest-only'],
      },
      {
        id: 'section-physical-details',
        title: 'Physical Details',
        fieldIds: ['field-number-of-units', 'field-market-value', 'field-property-sqft', 'field-lot-size-acres'],
      },
    ],
  };
};

/**
 * Generate per-unit rent/status fields for units 1-10.
 * Uses the counter pattern: numberOfUnits from the Property Details step
 * drives conditional display of per-unit fields.
 */
const createDscrUnitsRentsStep = (): IntakeStepTemplate => {
  const unitFields: IntakeFieldTemplate[] = [];
  const sectionFieldIds: Record<number, string[]> = {};

  for (let n = 1; n <= 10; n++) {
    const unitFieldIds: string[] = [];

    const statusId = `field-unit${n}-status`;
    const leaseTypeId = `field-unit${n}-lease-type`;
    const familyLeaseId = `field-unit${n}-family-lease`;
    const monthlyRentId = `field-unit${n}-monthly-rent`;
    const marketRentId = `field-unit${n}-market-rent`;

    unitFieldIds.push(statusId, leaseTypeId, familyLeaseId, monthlyRentId, marketRentId);

    const unitCondition = { field: 'numberOfUnits', operator: 'greater_than_or_equals' as const, value: String(n) };

    unitFields.push(
      {
        id: statusId,
        name: `unit${n}Status`,
        label: `Unit ${n} Status`,
        type: 'select',
        required: false,
        options: [
          { value: 'leased', label: 'Leased' },
          { value: 'vacant', label: 'Vacant' },
          { value: 'owner_occupied', label: 'Owner-Occupied' },
        ],
        conditionalDisplay: unitCondition,
        width: 4,
      },
      {
        id: leaseTypeId,
        name: `unit${n}LeaseType`,
        label: `Unit ${n} Lease Type`,
        type: 'select',
        required: false,
        options: [
          { value: 'long_term', label: 'Long-Term' },
          { value: 'short_term', label: 'Short-Term' },
          { value: 'month_to_month', label: 'Month-to-Month' },
          { value: 'section_8', label: 'Section 8' },
          { value: 'sro', label: 'SRO' },
        ],
        conditionalDisplayGroup: {
          logic: 'AND',
          conditions: [unitCondition, { field: `unit${n}Status`, operator: 'equals', value: 'leased' }],
        },
        width: 4,
      },
      {
        id: familyLeaseId,
        name: `unit${n}FamilyLease`,
        label: `Unit ${n} Leased to Family Member?`,
        type: 'radio',
        required: false,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        conditionalDisplayGroup: {
          logic: 'AND',
          conditions: [unitCondition, { field: `unit${n}Status`, operator: 'equals', value: 'leased' }],
        },
        width: 4,
      },
      {
        id: monthlyRentId,
        name: `unit${n}MonthlyRent`,
        label: `Unit ${n} Monthly Rent`,
        type: 'currency',
        required: false,
        placeholder: '$0',
        conditionalDisplayGroup: {
          logic: 'AND',
          conditions: [unitCondition, { field: `unit${n}Status`, operator: 'equals', value: 'leased' }],
        },
        width: 6,
      },
      {
        id: marketRentId,
        name: `unit${n}MarketRent`,
        label: `Unit ${n} Estimated Market Rent`,
        type: 'currency',
        required: false,
        placeholder: '$0',
        helpText: 'Market rent estimate for vacant or owner-occupied units',
        conditionalDisplayGroup: {
          logic: 'AND',
          conditions: [unitCondition, { field: `unit${n}Status`, operator: 'not_equals', value: 'leased' }],
        },
        width: 6,
      },
    );

    sectionFieldIds[n] = unitFieldIds;
  }

  return {
    id: 'step-dscr-units-rents',
    order: 6,
    name: 'Units & Rents',
    description: 'Enter rent details for each unit',
    icon: 'Apartment',
    requiresAuth: false,
    fields: [
      ...unitFields,
      // Property risk sub-section fields
      {
        id: 'field-has-adus',
        name: 'hasADUs',
        label: 'Does the property have any ADUs (Accessory Dwelling Units)?',
        type: 'radio',
        required: true,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        width: 12,
      },
      {
        id: 'field-has-deferred-maintenance',
        name: 'hasDeferredMaintenance',
        label: 'Is there deferred maintenance?',
        type: 'radio',
        required: true,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        width: 6,
      },
      {
        id: 'field-is-nonconforming',
        name: 'isNonconforming',
        label: 'Is the property nonconforming?',
        type: 'radio',
        required: true,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Nonconforming properties do not meet current zoning requirements',
        width: 6,
      },
    ],
    sections: [
      ...Array.from({ length: 10 }, (_, i) => ({
        id: `section-unit-${i + 1}`,
        title: `Unit ${i + 1}`,
        conditionalDisplay: {
          field: 'numberOfUnits',
          operator: 'greater_than_or_equals' as const,
          value: String(i + 1),
        },
        fieldIds: sectionFieldIds[i + 1],
      })),
      {
        id: 'section-property-risk',
        title: 'Property Risk Assessment',
        fieldIds: ['field-has-adus', 'field-has-deferred-maintenance', 'field-is-nonconforming'],
      },
    ],
  };
};

const createDscrHousingExpensesStep = (): IntakeStepTemplate => ({
  id: 'step-dscr-housing-expenses',
  order: 7,
  name: 'Housing Expenses',
  description: 'Enter annual housing expenses for DSCR calculation',
  icon: 'Receipt',
  requiresAuth: false,
  fields: [
    {
      id: 'field-annual-property-taxes',
      name: 'annualPropertyTaxes',
      label: 'Annual Property Taxes',
      type: 'currency',
      required: true,
      placeholder: '$0',
      width: 6,
    },
    {
      id: 'field-annual-property-insurance',
      name: 'annualPropertyInsurance',
      label: 'Annual Property Insurance',
      type: 'currency',
      required: true,
      placeholder: '$0',
      width: 6,
    },
    {
      id: 'field-has-hoa',
      name: 'hasHOA',
      label: 'Is there an HOA?',
      type: 'radio',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      width: 6,
    },
    {
      id: 'field-annual-hoa-fees',
      name: 'annualHOAFees',
      label: 'Annual HOA Fees',
      type: 'currency',
      required: false,
      placeholder: '$0',
      conditionalDisplay: { field: 'hasHOA', operator: 'equals', value: 'yes' },
      width: 6,
    },
    {
      id: 'field-flood-insurance-required',
      name: 'floodInsuranceRequired',
      label: 'Is flood insurance required?',
      type: 'radio',
      required: true,
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      width: 6,
    },
    {
      id: 'field-annual-flood-insurance',
      name: 'annualFloodInsurance',
      label: 'Annual Flood Insurance',
      type: 'currency',
      required: false,
      placeholder: '$0',
      conditionalDisplay: { field: 'floodInsuranceRequired', operator: 'equals', value: 'yes' },
      width: 6,
    },
  ],
});

const createDscrRateCalculatorStep = (): IntakeStepTemplate => {
  const baseStep = createRateCalculatorStep({
    maxLTV: 80,
    maxLTC: 80,
    defaultRate: 8.5,
    minLoanAmount: 75000,
    maxLoanAmount: 5000000,
  });
  return {
    ...baseStep,
    fields: [
      // Purchase Price — only shown for purchase loans
      {
        ...baseStep.fields.find((f) => f.name === 'purchasePrice')!,
        conditionalDisplay: { field: 'loanPurpose', operator: 'equals', value: 'purchase' },
      },
      // ARV (keep as-is)
      baseStep.fields.find((f) => f.name === 'afterRepairValue')!,
      // Expected Monthly Rent (DSCR-specific)
      {
        id: 'field-monthly-rent',
        name: 'expectedMonthlyRent',
        label: 'Expected Monthly Rent',
        type: 'currency',
        required: true,
        validation: { min: 500, max: 100000 },
        placeholder: '$0',
        helpText: 'Current or expected total rental income across all units',
        width: 6,
      },
      // Loan Amount (keep as-is)
      baseStep.fields.find((f) => f.name === 'requestedLoanAmount')!,
      // Credit Score (keep as-is)
      baseStep.fields.find((f) => f.name === 'estimatedFico')!,
      // DSCR-specific term options (not bridge terms)
      {
        id: 'field-term',
        name: 'preferredTerm',
        label: 'Preferred Loan Term',
        type: 'select',
        required: true,
        defaultValue: '360',
        options: [
          { value: '60', label: '5 Year' },
          { value: '84', label: '7 Year' },
          { value: '120', label: '10 Year' },
          { value: '360', label: '30 Year Fixed' },
        ],
        width: 6,
      },
      // Desired LTV
      {
        id: 'field-desired-ltv',
        name: 'desiredLTV',
        label: 'Desired LTV',
        type: 'select',
        required: true,
        options: [
          { value: '65', label: '65%' },
          { value: '70', label: '70%' },
          { value: '75', label: '75%' },
          { value: '80', label: '80% (Maximum Allowed)' },
        ],
        helpText: 'Maximum LTV varies by credit score and property type',
        width: 6,
      },
      // Prepayment penalty term
      {
        id: 'field-prepayment-penalty-term',
        name: 'prepaymentPenaltyTerm',
        label: 'Prepayment Penalty Term',
        type: 'select',
        required: true,
        options: [
          { value: 'none', label: 'None' },
          { value: '1_year', label: '1 Year' },
          { value: '2_year', label: '2 Year' },
          { value: '3_year', label: '3 Year' },
          { value: '4_year', label: '4 Year' },
          { value: '5_year', label: '5 Year' },
        ],
        width: 6,
      },
      // Prepayment penalty structure (conditional)
      {
        id: 'field-prepayment-penalty-structure',
        name: 'prepaymentPenaltyStructure',
        label: 'Prepayment Penalty Structure',
        type: 'select',
        required: false,
        options: [
          { value: 'step_down', label: 'Step-Down' },
          { value: 'flat', label: 'Flat' },
          { value: 'yield_maintenance', label: 'Yield Maintenance' },
        ],
        conditionalDisplay: { field: 'prepaymentPenaltyTerm', operator: 'not_equals', value: 'none' },
        width: 6,
      },
    ],
  };
};

// =============================================================================
// DSCR RENTAL TEMPLATE
// =============================================================================

export const RENTAL_DSCR_TEMPLATE: IntakeFlowTemplate = {
  id: INTAKE_FLOW_TEMPLATE_IDS.RENTAL_DSCR,
  name: 'DSCR Rental Loan',
  description: 'Long-term financing for rental properties based on property cash flow.',
  category: 'rental',
  icon: 'Home',
  color: '#2196F3',
  tags: ['dscr', 'rental', 'long-term', 'cash-flow', 'buy-and-hold'],
  estimatedDuration: '10-15 minutes',
  complexity: 'intermediate',
  industryFit: ['real_estate', 'lending'],
  data: {
    intakeType: 'rental',
    version: '2.0.0',
    displayMode: IntakeDisplayMode.SCROLL,
    steps: [
      createInvestmentTypeStep(), // Step 1: Investment Type
      createDscrContactInfoStep(), // Step 2: Contact Info (enhanced)
      createDscrLoanStructureStep(), // Step 3: Loan Structure (NEW)
      createEntityExperienceStep(), // Step 4: Entity & Experience
      createCoBorrowerStep(), // Step 5: Co-Borrower
      createDscrPropertyDetailsStep(), // Step 6: Property Details (enhanced)
      createDscrUnitsRentsStep(), // Step 7: Units & Rents (NEW)
      createDscrHousingExpensesStep(), // Step 8: Housing Expenses (NEW)
      createDscrRateCalculatorStep(), // Step 9: Rate Calculator (enhanced)
      // Compliance handled via complianceQuestions property (bottom checkbox section)
      createSigningDateStep(), // Step 10: Signing Date
      createGuarantorDetailsStep(), // Step 11: Guarantor Details
      createSsnVerificationStep(), // Step 12: SSN Verification
    ].map((step, index) => ({ ...step, order: index })),
    rateCalculatorConfig: {
      maxLTV: 80,
      maxLTC: 80,
      termOptions: [60, 84, 120, 360],
      defaultRate: 8.5,
      minLoanAmount: 75000,
      maxLoanAmount: 5000000,
      rateAdjustments: [
        { condition: 'fico >= 760', adjustment: -0.5, description: 'Excellent credit discount' },
        { condition: 'ltv <= 65', adjustment: -0.25, description: 'Low LTV discount' },
        { condition: 'dscr >= 1.5', adjustment: -0.25, description: 'Strong DSCR discount' },
        { condition: 'prepayment >= 3yr', adjustment: -0.375, description: 'Prepayment penalty discount' },
      ],
    },
    complianceQuestions: STANDARD_COMPLIANCE_QUESTIONS,
    exitBehavior: 'case_creation',
  },
};

// =============================================================================
// COMMERCIAL TEMPLATE
// =============================================================================

export const COMMERCIAL_TEMPLATE: IntakeFlowTemplate = {
  id: INTAKE_FLOW_TEMPLATE_IDS.COMMERCIAL,
  name: 'Commercial Loan',
  description: 'Financing for commercial real estate investments.',
  category: 'commercial',
  icon: 'Business',
  color: '#9C27B0',
  tags: ['commercial', 'office', 'retail', 'industrial', 'mixed-use'],
  estimatedDuration: '10-15 minutes',
  complexity: 'advanced',
  industryFit: ['real_estate', 'lending', 'fintech'],
  data: {
    intakeType: 'commercial',
    version: '1.0.0',
    displayMode: IntakeDisplayMode.SCROLL,
    steps: [
      createInvestmentTypeStep(),
      createContactInfoStep(),
      createEntityExperienceStep(),
      createCoBorrowerStep(),
      {
        ...createPropertyAddressStep(),
        fields: [
          ...createPropertyAddressStep().fields.filter((f) => f.name !== 'propertyType'),
          {
            id: 'field-commercial-property-type',
            name: 'propertyType',
            label: 'Commercial Property Type',
            type: 'select',
            required: true,
            options: [
              { value: 'office', label: 'Office' },
              { value: 'retail', label: 'Retail' },
              { value: 'industrial', label: 'Industrial/Warehouse' },
              { value: 'mixed_use', label: 'Mixed Use' },
              { value: 'multifamily', label: 'Multifamily (5+ units)' },
              { value: 'hospitality', label: 'Hospitality' },
              { value: 'self_storage', label: 'Self Storage' },
              { value: 'other', label: 'Other' },
            ],
            width: 6,
          },
        ],
      },
      createRateCalculatorStep({
        maxLTV: 70,
        maxLTC: 75,
        defaultRate: 9.5,
        minLoanAmount: 500000,
        maxLoanAmount: 25000000,
      }),
      // Compliance handled via complianceQuestions property (bottom checkbox section)
      createSigningDateStep(),
      createGuarantorDetailsStep(),
      createSsnVerificationStep(),
    ],
    rateCalculatorConfig: {
      maxLTV: 70,
      maxLTC: 75,
      termOptions: [60, 120, 180, 240, 300],
      defaultRate: 9.5,
      minLoanAmount: 500000,
      maxLoanAmount: 25000000,
    },
    complianceQuestions: STANDARD_COMPLIANCE_QUESTIONS,
    exitBehavior: 'case_creation',
  },
};

// =============================================================================
// GENERAL / CONSULTATION TEMPLATE
// =============================================================================

export const GENERAL_TEMPLATE: IntakeFlowTemplate = {
  id: INTAKE_FLOW_TEMPLATE_IDS.GENERAL,
  name: 'General Consultation',
  description: 'Not sure which loan type? Start here to speak with an advisor.',
  category: 'general',
  icon: 'Support',
  color: '#607D8B',
  tags: ['consultation', 'advisor', 'general', 'help'],
  estimatedDuration: '3-5 minutes',
  complexity: 'beginner',
  industryFit: ['real_estate', 'lending', 'general'],
  data: {
    intakeType: 'general',
    version: '1.0.0',
    displayMode: IntakeDisplayMode.SCROLL,
    steps: [
      createInvestmentTypeStep(),
      {
        id: 'step-contact-info',
        order: 1,
        name: 'Contact Information',
        description: "Tell us how to reach you and we'll have an advisor contact you",
        icon: 'ContactPhone',
        requiresAuth: false,
        fields: [
          {
            id: 'field-first-name',
            name: 'firstName',
            label: 'First Name',
            type: 'text',
            required: true,
            width: 6,
          },
          {
            id: 'field-last-name',
            name: 'lastName',
            label: 'Last Name',
            type: 'text',
            required: true,
            width: 6,
          },
          {
            id: 'field-email',
            name: 'email',
            label: 'Email Address',
            type: 'email',
            required: true,
            width: 6,
          },
          {
            id: 'field-phone',
            name: 'phone',
            label: 'Phone Number',
            type: 'phone',
            required: true,
            width: 6,
          },
          {
            id: 'field-preferred-contact',
            name: 'preferredContactMethod',
            label: 'Preferred Contact Method',
            type: 'radio',
            required: true,
            options: [
              { value: 'phone', label: 'Phone' },
              { value: 'email', label: 'Email' },
              { value: 'text', label: 'Text Message' },
            ],
            width: 12,
          },
          {
            id: 'field-best-time',
            name: 'bestTimeToContact',
            label: 'Best Time to Contact',
            type: 'select',
            required: true,
            options: [
              { value: 'morning', label: 'Morning (9am - 12pm)' },
              { value: 'afternoon', label: 'Afternoon (12pm - 5pm)' },
              { value: 'evening', label: 'Evening (5pm - 8pm)' },
              { value: 'anytime', label: 'Anytime' },
            ],
            width: 6,
          },
        ],
      },
      {
        id: 'step-project-details',
        order: 2,
        name: 'Project Details',
        description: 'Tell us about your project',
        icon: 'Description',
        requiresAuth: false,
        fields: [
          {
            id: 'field-project-description',
            name: 'projectDescription',
            label: 'Briefly describe your project or financing needs',
            type: 'text',
            required: true,
            placeholder: 'e.g., Looking to purchase a duplex for rental income...',
            width: 12,
          },
          {
            id: 'field-estimated-amount',
            name: 'estimatedLoanAmount',
            label: 'Estimated Loan Amount Needed',
            type: 'select',
            required: true,
            options: [
              { value: 'under-100k', label: 'Under $100,000' },
              { value: '100k-250k', label: '$100,000 - $250,000' },
              { value: '250k-500k', label: '$250,000 - $500,000' },
              { value: '500k-1m', label: '$500,000 - $1,000,000' },
              { value: '1m-5m', label: '$1,000,000 - $5,000,000' },
              { value: 'over-5m', label: 'Over $5,000,000' },
              { value: 'not-sure', label: "I'm not sure" },
            ],
            width: 6,
          },
          {
            id: 'field-timeline',
            name: 'timeline',
            label: 'When do you need funding?',
            type: 'select',
            required: true,
            options: [
              { value: 'asap', label: 'As soon as possible' },
              { value: '1-month', label: 'Within 1 month' },
              { value: '1-3-months', label: '1-3 months' },
              { value: '3-6-months', label: '3-6 months' },
              { value: 'just-exploring', label: 'Just exploring options' },
            ],
            width: 6,
          },
        ],
      },
    ],
    complianceQuestions: [],
    exitBehavior: 'advisor_redirect',
    advisorRedirectUrl: '/thank-you?type=consultation',
  },
};

// =============================================================================
// ALL TEMPLATES ARRAY
// =============================================================================

export const INTAKE_FLOW_TEMPLATES: IntakeFlowTemplate[] = [
  BRIDGE_FIX_FLIP_TEMPLATE,
  CONSTRUCTION_TEMPLATE,
  RENTAL_DSCR_TEMPLATE,
  COMMERCIAL_TEMPLATE,
  GENERAL_TEMPLATE,
];

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get all intake flow templates
 */
export function getAllIntakeFlowTemplates(): IntakeFlowTemplate[] {
  return INTAKE_FLOW_TEMPLATES;
}

/**
 * Get intake flow template by ID
 */
export function getIntakeFlowTemplateById(id: string): IntakeFlowTemplate | undefined {
  return INTAKE_FLOW_TEMPLATES.find((t) => t.id === id);
}

/**
 * Get intake flow template by intake type
 */
export function getIntakeFlowTemplateByType(intakeType: IntakeFlowType): IntakeFlowTemplate | undefined {
  return INTAKE_FLOW_TEMPLATES.find((t) => t.data.intakeType === intakeType);
}

/**
 * Get intake flow templates by category
 */
export function getIntakeFlowTemplatesByCategory(category: string): IntakeFlowTemplate[] {
  return INTAKE_FLOW_TEMPLATES.filter((t) => t.category === category);
}

/**
 * Search intake flow templates
 */
export function searchIntakeFlowTemplates(query: string): IntakeFlowTemplate[] {
  const lowerQuery = query.toLowerCase();
  return INTAKE_FLOW_TEMPLATES.filter(
    (t) =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)),
  );
}
